import { css } from '@emotion/react';
import classNames from 'classnames';
import React, { ReactNode } from 'react';
import { semantic_colors, spacing, text_styles } from '../../foundation';
import { getAutoColorCode } from '../../styles';
import { IconTune } from '../icons/generated';
import { ButtonIcon } from './ButtonIcon';
import { button_base_css, button_size_css, button_size_icon_size_css, getButtonStyle } from './styles';
import type { ButtonProps } from './Button';

export interface FilterButtonProps
  extends Omit<ButtonProps, 'asChild' | 'fill' | 'loading' | 'variant' | 'color' | 'size' | 'leftIcon' | 'rightIcon'> {
  children: ReactNode;
  /** @default false */
  selected?: boolean;
  /**
   * `selected` 상태에서 표시할 변경 또는 선택한 필터 개수
   * @default 0
   */
  count?: number;
}

export const FilterButton = React.forwardRef<HTMLButtonElement, FilterButtonProps>(
  ({ children, selected = false, count = 0, type = 'button', disabled, className, ...props }, ref) => {
    return (
      <button
        type={type}
        className={classNames('pds-button', 'pds-filter-button', className)}
        css={[
          button_base_css,
          getButtonStyle('outlined', selected ? 'primary' : 'secondary'),
          button_size_css.xsmall,
          getFilterButtonBackgroundStyle(selected),
          css`
            cursor: ${disabled ? 'not-allowed' : 'pointer'};
          `,
        ]}
        disabled={disabled}
        {...props}
        ref={ref}
      >
        <ButtonIcon size={button_size_icon_size_css.xsmall} color='currentColor'>
          <IconTune />
        </ButtonIcon>
        <span css={filter_button_content_css}>
          {children}
          {selected && <span css={filter_button_count_css}>{count.toLocaleString()}</span>}
        </span>
      </button>
    );
  },
);

const filter_button_content_css = css`
  display: inline-flex;
  align-items: center;
  column-gap: ${spacing.spacing_1};
`;

const filter_button_count_css = css`
  display: inline-flex;
  box-sizing: border-box;
  min-width: ${spacing.spacing_4};
  height: ${spacing.spacing_4};
  padding: 0 ${spacing.spacing_1};
  flex: none;
  align-items: center;
  justify-content: center;
  border-radius: ${spacing.spacing_25};
  color: ${semantic_colors.static.white};
  background-color: ${semantic_colors.accent.primary};
  ${text_styles.Caption_11_SemiBold};
`;

const getFilterButtonBackgroundStyle = (selected: boolean) => {
  const background_color = selected ? semantic_colors.accent.light : semantic_colors.background.surface;

  return css`
    background-color: ${background_color};

    &:hover:not(:disabled) {
      background-color: ${getAutoColorCode(background_color).hover};
    }

    &:active:not(:disabled) {
      background-color: ${getAutoColorCode(background_color).pressed};
    }
  `;
};
